import {Group} from '@mantine/core';
import React from 'react';

import {hasUnsavedChanges} from 'notebook/entries/BaseEntryUtil';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import {useStableCyclicalOpenVersionId} from 'notebook/reviews/hooks/useStableCyclicalOpenVersionId';
import postTemplateReviewActionServerUpdate from 'notebook/reviews/postTemplateReviewActionServerUpdate';
import ReviewBadge from 'notebook/reviews/ReviewBadge';
import {useReviewController} from 'notebook/reviews/useReviewController';
import TemplatePublishingButton, {
  TemplatePublishingButtonProps,
} from 'template-collectibles/publishing-ui/TemplatePublishingButton';
import {getPublishRequiresApprovalTooltip} from 'template-collectibles/publishing-ui/TemplatePublishingConstants';
import APIUtil from 'util/APIUtil';
import {capitalize} from 'util/NameUtil';

export interface TemplateNoteTitleBadgesProps {
  // A template or sub-template id.
  templateId: string;
  openVersionApiId: string | undefined;
  isLatest: boolean;
  isViewingPublishedVersion: boolean;
  publishingButtonProps: TemplatePublishingButtonProps;
}

function getTemplateCollectibleNoun(templateId: string): string {
  return APIUtil.isSubtemplate(templateId) ? 'sub-template' : 'template';
}

function getTemplateSendForReviewValidationError(templateId: string): string | null {
  const noun = capitalize(getTemplateCollectibleNoun(templateId));
  const isInStore = APIUtil.isSubtemplate(templateId)
    ? SubtemplateStore.inStore(templateId)
    : TemplateStore.inStore(templateId);
  if (!isInStore) {
    return `${noun} could not be found`;
  }
  if (hasUnsavedChanges(templateId)) {
    return `${noun} has unsaved changes`;
  }
  return null;
}

export default function TemplateNoteTitleBadges({
  templateId,
  openVersionApiId,
  isLatest,
  isViewingPublishedVersion,
  publishingButtonProps,
}: TemplateNoteTitleBadgesProps): JSX.Element {
  const resolvedOpenVersionApiId = useStableCyclicalOpenVersionId({
    templateId,
    openVersionApiId,
    isLatest,
    isViewingPublishedVersion,
  });

  const isCurrentEditableDraft = isLatest && !isViewingPublishedVersion;
  const controller = useReviewController(templateId, {
    enableReviewLifecycle: true,
    onReviewChange: postTemplateReviewActionServerUpdate,
    openVersionId: resolvedOpenVersionApiId,
    isCurrentEditableDraft,
    isViewingPublishedVersion,
    getSendForReviewValidationError: getTemplateSendForReviewValidationError,
    cyclicalPublishGating: {
      isLatest,
      isPublishOptionDisabledFromParent: publishingButtonProps.isPublishOptionDisabled,
    },
  });

  const publishState = controller.publishState;
  // Only mount the badge when the controller has settled data; resolvedOpenVersionApiId covers transient undefined version ids.
  const showReviewBadge =
    resolvedOpenVersionApiId != null &&
    publishState != null &&
    !publishState.error &&
    !publishState.inaccessible &&
    controller.state === 'READY';

  return (
    <Group spacing={8} align="center" display="inline-flex">
      {showReviewBadge && <ReviewBadge reviewableId={templateId} controller={controller} />}
      <TemplatePublishingButton
        {...publishingButtonProps}
        isPublishOptionDisabled={
          publishState?.isPublishOptionDisabled ?? publishingButtonProps.isPublishOptionDisabled
        }
        publishDisabledTooltip={
          publishState?.isPublishBlockedByReview
            ? getPublishRequiresApprovalTooltip(getTemplateCollectibleNoun(templateId))
            : undefined
        }
      />
    </Group>
  );
}
